import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Building, MapPin, Upload, Image, FileText,
  Layers, Home, ChevronDown, ChevronUp, X, Eye, GripVertical
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { AiTextHelper } from '../../components/AiTextHelper';
import MultilingualDescriptionEditor from '../../components/MultilingualDescriptionEditor';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProperties() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [propertyModal, setPropertyModal] = useState({ open: false, data: null });
  const [buildingModal, setBuildingModal] = useState({ open: false, data: null, propertyId: null });
  const [floorModal, setFloorModal] = useState({ open: false, data: null, buildingId: null });
  const [apartmentModal, setApartmentModal] = useState({ open: false, data: null, floorId: null, buildingId: null, propertyId: null });
  const [uploadModal, setUploadModal] = useState({ open: false, category: '', entityId: '' });
  
  // Expanded states
  const [expandedProperties, setExpandedProperties] = useState({});
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [expandedFloors, setExpandedFloors] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [propRes, buildRes, floorRes, aptRes] = await Promise.allSettled([
        axios.get(`${API}/properties`),
        axios.get(`${API}/buildings`),
        axios.get(`${API}/floors`),
        axios.get(`${API}/apartments`),
      ]);
      if (propRes.status === 'fulfilled') setProperties(propRes.value.data || []);
      if (buildRes.status === 'fulfilled') setBuildings(buildRes.value.data || []);
      if (floorRes.status === 'fulfilled') setFloors(floorRes.value.data || []);
      if (aptRes.status === 'fulfilled') setApartments(aptRes.value.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // File upload handler
  const handleFileUpload = async (e, category, entityId) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/upload?category=${category}&entity_id=${entityId}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Файлът е качен успешно');
      fetchData();
      return response.data;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Грешка при качване на файла');
      return null;
    }
  };

  // Get files for entity
  const getFilesForEntity = (entityId) => {
    return files.filter(f => f.entity_id === entityId);
  };

  // CRUD handlers for Property
  const handlePropertySubmit = async (formData) => {
    try {
      if (propertyModal.data) {
        await axios.put(`${API}/properties/${propertyModal.data.id}`, formData);
        toast.success('Проектът е обновен');
      } else {
        const res = await axios.post(`${API}/properties`, formData);
        // If images were uploaded before save (entity_id='new'), reassign them
        const newId = res.data?.id;
        if (newId) {
          await axios.put(`${API}/files/reassign`, { old_entity_id: 'new', new_entity_id: newId }).catch(() => {});
        }
        toast.success('Проектът е създаден');
      }
      setPropertyModal({ open: false, data: null });
      fetchData();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDeleteProperty = async (id) => {
    if (!window.confirm('Изтриване на проекта и всички свързани данни?')) return;
    try {
      await axios.delete(`${API}/properties/${id}`);
      toast.success('Проектът е изтрит');
      fetchData();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  // CRUD handlers for Building
  const handleBuildingSubmit = async (formData) => {
    try {
      if (buildingModal.data) {
        await axios.put(`${API}/buildings/${buildingModal.data.id}`, formData);
        toast.success('Сградата е обновена');
      } else {
        await axios.post(`${API}/buildings`, formData);
        toast.success('Сградата е създадена');
      }
      setBuildingModal({ open: false, data: null, propertyId: null });
      fetchData();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDeleteBuilding = async (id) => {
    if (!window.confirm('Изтриване на сградата и всички етажи/апартаменти?')) return;
    try {
      await axios.delete(`${API}/buildings/${id}`);
      toast.success('Сградата е изтрита');
      fetchData();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  // CRUD handlers for Floor
  const handleFloorSubmit = async (formData) => {
    try {
      if (floorModal.data) {
        await axios.put(`${API}/floors/${floorModal.data.id}`, formData);
        toast.success('Етажът е обновен');
      } else {
        await axios.post(`${API}/floors`, formData);
        toast.success('Етажът е създаден');
      }
      setFloorModal({ open: false, data: null, buildingId: null });
      fetchData();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDeleteFloor = async (id) => {
    if (!window.confirm('Изтриване на етажа и всички апартаменти?')) return;
    try {
      await axios.delete(`${API}/floors/${id}`);
      toast.success('Етажът е изтрит');
      fetchData();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  // CRUD handlers for Apartment
  const handleApartmentSubmit = async (formData) => {
    try {
      if (apartmentModal.data) {
        await axios.put(`${API}/apartments/${apartmentModal.data.id}`, formData);
        toast.success('Апартаментът е обновен');
      } else {
        await axios.post(`${API}/apartments`, formData);
        toast.success('Апартаментът е създаден');
      }
      setApartmentModal({ open: false, data: null, floorId: null, buildingId: null, propertyId: null });
      fetchData();
    } catch (error) {
      toast.error('Грешка при запазване');
    }
  };

  const handleDeleteApartment = async (id) => {
    if (!window.confirm('Изтриване на апартамента?')) return;
    try {
      await axios.delete(`${API}/apartments/${id}`);
      toast.success('Апартаментът е изтрит');
      fetchData();
    } catch (error) {
      toast.error('Грешка при изтриване');
    }
  };

  const statusLabels = { active: t('props.status.active'), completed: t('props.status.completed'), upcoming: t('props.status.upcoming') };
  const statusColors = { active: 'bg-yellow-100 text-yellow-800', completed: 'bg-green-100 text-green-800', upcoming: 'bg-blue-100 text-blue-800' };
  const aptStatusLabels = { available: t('props.apt.available'), reserved: t('props.apt.reserved'), sold: t('props.apt.sold') };
  const aptStatusColors = { available: 'bg-green-100 text-green-800', reserved: 'bg-yellow-100 text-yellow-800', sold: 'bg-red-100 text-red-800' };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;
  }

  return (
    <div data-testid="admin-properties">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">{t('props.page_title')}</h1>
          <p className="text-muted-foreground">Проекти, сгради, етажи и апартаменти</p>
        </div>
        <Button onClick={() => setPropertyModal({ open: true, data: null })} data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-2" />
          {t('props.new_project')}
        </Button>
      </div>

      {/* Properties List */}
      {properties.length === 0 ? (
        <div className="admin-card text-center py-12">
          <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Няма добавени проекти</p>
        </div>
      ) : (
        <div className="space-y-4">
          {properties.map((property) => (
            <div key={property.id} className="admin-card">
              {/* Property Header */}
              <div className="flex items-start gap-4">
                <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 group/thumb">
                  <img
                    src={property.images?.[0] || property.main_image || 'https://images.unsplash.com/photo-1758193431355-54df41421657'}
                    alt={property.name}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover/thumb:opacity-0"
                  />
                  {property.images?.[1] && (
                    <img
                      src={property.images[1]}
                      alt={property.name}
                      className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 opacity-0 group-hover/thumb:opacity-100"
                    />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-heading font-semibold text-lg">{property.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {property.location}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={statusColors[property.status]}>{statusLabels[property.status]}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => setPropertyModal({ open: true, data: property })}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteProperty(property.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" asChild title={t('admin.preview')}>
                        <a href={`/properties/${property.id}`} target="_blank" rel="noopener noreferrer">
                          <Eye className="w-4 h-4" />
                        </a>
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setExpandedProperties(p => ({ ...p, [property.id]: !p[property.id] }))}
                      >
                        {expandedProperties[property.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Property stats */}
                  <div className="flex items-center gap-2 mt-2">
                    {(() => {
                      const imgCount = property.images?.length || 0;
                      const docCount = property.doc_count || 0;
                      const bldCount = buildings.filter(b => b.property_id === property.id).length;
                      const badge = (count, icon, label, color) => (
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                          count > 0 ? color : 'bg-muted text-muted-foreground'
                        }`}>
                          {icon}{count} {label}
                        </span>
                      );
                      return (<>
                        {badge(imgCount, <Image className="w-3 h-3" />, 'снимки', 'bg-blue-100 text-blue-700')}
                        {badge(docCount, <FileText className="w-3 h-3" />, 'документа', 'bg-amber-100 text-amber-700')}
                        {badge(bldCount, <Building className="w-3 h-3" />, 'сгради', 'bg-green-100 text-green-700')}
                      </>);
                    })()}
                  </div>
                </div>
              </div>

              {/* Expanded: Buildings */}
              {expandedProperties[property.id] && (
                <div className="mt-6 pl-4 border-l-2 border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium flex items-center gap-2">
                      <Building className="w-4 h-4 text-primary" />
                      Сгради ({buildings.filter(b => b.property_id === property.id).length})
                    </h4>
                    <Button size="sm" variant="outline" onClick={() => setBuildingModal({ open: true, data: null, propertyId: property.id })}>
                      <Plus className="w-3 h-3 mr-1" /> Сграда
                    </Button>
                  </div>

                  {buildings.filter(b => b.property_id === property.id).map((building) => (
                    <div key={building.id} className="bg-secondary/50 rounded-lg p-4 mb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Building className="w-5 h-5 text-primary" />
                          <div>
                            <span className="font-medium">{building.name}</span>
                            <span className="text-sm text-muted-foreground ml-2">({building.floors_count} етажа)</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <label className="cursor-pointer">
                            <input type="file" className="hidden" accept="image/*"
                              onChange={(e) => handleFileUpload(e, 'building', building.id)} />
                            <span className="inline-flex items-center p-1 hover:bg-white rounded" title="Качи чертеж">
                              <Upload className="w-4 h-4 text-muted-foreground" />
                            </span>
                          </label>
                          <Button variant="ghost" size="sm" onClick={() => setBuildingModal({ open: true, data: building, propertyId: property.id })}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteBuilding(building.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setExpandedBuildings(b => ({ ...b, [building.id]: !b[building.id] }))}>
                            {expandedBuildings[building.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>

                      {/* Expanded: Floors */}
                      {expandedBuildings[building.id] && (
                        <div className="mt-4 pl-4 border-l border-border">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium flex items-center gap-1">
                              <Layers className="w-3 h-3" /> Етажи
                            </span>
                            <Button size="sm" variant="ghost" onClick={() => setFloorModal({ open: true, data: null, buildingId: building.id })}>
                              <Plus className="w-3 h-3 mr-1" /> Етаж
                            </Button>
                          </div>

                          {floors.filter(f => f.building_id === building.id).sort((a, b) => a.floor_number - b.floor_number).map((floor) => (
                            <div key={floor.id} className="bg-white rounded-lg p-3 mb-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Layers className="w-4 h-4 text-muted-foreground" />
                                  <span className="font-medium">Етаж {floor.floor_number}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({apartments.filter(a => a.floor_id === floor.id).length} апартамента)
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <label className="cursor-pointer">
                                    <input type="file" className="hidden" accept="image/*"
                                      onChange={(e) => handleFileUpload(e, 'floor', floor.id)} />
                                    <span className="inline-flex p-1 hover:bg-secondary rounded" title="Качи план">
                                      <Upload className="w-3 h-3 text-muted-foreground" />
                                    </span>
                                  </label>
                                  <Button variant="ghost" size="sm" onClick={() => setFloorModal({ open: true, data: floor, buildingId: building.id })}>
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteFloor(floor.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setExpandedFloors(f => ({ ...f, [floor.id]: !f[floor.id] }))}>
                                    {expandedFloors[floor.id] ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                  </Button>
                                </div>
                              </div>

                              {/* Expanded: Apartments */}
                              {expandedFloors[floor.id] && (
                                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                                  {apartments.filter(a => a.floor_id === floor.id).map((apt) => (
                                    <div key={apt.id} className={`p-2 rounded border ${aptStatusColors[apt.status].replace('text-', 'border-').replace('-800', '-300')}`}>
                                      <div className="flex items-center justify-between">
                                        <span className="font-medium text-sm">Ап. {apt.number}</span>
                                        <Badge className={`text-xs ${aptStatusColors[apt.status]}`}>{aptStatusLabels[apt.status]}</Badge>
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-1">
                                        {apt.area} м² • {apt.rooms} стаи • €{apt.price.toLocaleString()}
                                      </div>
                                      <div className="flex items-center gap-1 mt-2">
                                        <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => setApartmentModal({ open: true, data: apt, floorId: floor.id, buildingId: building.id, propertyId: property.id })}>
                                          <Edit className="w-3 h-3" />
                                        </Button>
                                        <label className="cursor-pointer">
                                          <input type="file" className="hidden" accept="image/*"
                                            onChange={(e) => handleFileUpload(e, 'apartment', apt.id)} />
                                          <span className="inline-flex p-1 hover:bg-secondary rounded">
                                            <Upload className="w-3 h-3" />
                                          </span>
                                        </label>
                                        <Button variant="ghost" size="sm" className="h-6 px-1 text-red-600" onClick={() => handleDeleteApartment(apt.id)}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                  <button 
                                    className="p-4 rounded border-2 border-dashed border-border hover:border-primary text-muted-foreground hover:text-primary transition-colors"
                                    onClick={() => setApartmentModal({ open: true, data: null, floorId: floor.id, buildingId: building.id, propertyId: property.id })}
                                  >
                                    <Plus className="w-5 h-5 mx-auto" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Property Modal */}
      <PropertyModal 
        key={propertyModal.data?.id || 'new'}
        open={propertyModal.open} 
        data={propertyModal.data}
        onClose={() => setPropertyModal({ open: false, data: null })}
        onSubmit={handlePropertySubmit}
      />

      {/* Building Modal */}
      <BuildingModal
        open={buildingModal.open}
        data={buildingModal.data}
        propertyId={buildingModal.propertyId}
        onClose={() => setBuildingModal({ open: false, data: null, propertyId: null })}
        onSubmit={handleBuildingSubmit}
      />

      {/* Floor Modal */}
      <FloorModal
        open={floorModal.open}
        data={floorModal.data}
        buildingId={floorModal.buildingId}
        onClose={() => setFloorModal({ open: false, data: null, buildingId: null })}
        onSubmit={handleFloorSubmit}
      />

      {/* Apartment Modal */}
      <ApartmentModal
        open={apartmentModal.open}
        data={apartmentModal.data}
        floorId={apartmentModal.floorId}
        buildingId={apartmentModal.buildingId}
        propertyId={apartmentModal.propertyId}
        onClose={() => setApartmentModal({ open: false, data: null, floorId: null, buildingId: null, propertyId: null })}
        onSubmit={handleApartmentSubmit}
      />
    </div>
  );
}

// Property Modal Component

// Reusable markets selector
const MARKET_OPTIONS = [
  { code: 'bg', label: '🇧🇬 България' },
  { code: 'gr', label: '🇬🇷 Гърция' },
  { code: 'tr', label: '🇹🇷 Турция' },
  { code: 'en', label: '🌍 Global (EN)' },
];

function MarketsSelector({ value, onChange }) {
  const markets = value || ['bg', 'gr', 'tr', 'en']; // default: all markets
  return (
    <div>
      <Label className="mb-2 block font-medium">Пазари (Markets)</Label>
      <div className="flex flex-wrap gap-3">
        {MARKET_OPTIONS.map(m => (
          <label key={m.code} className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={markets.includes(m.code)}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...new Set([...markets, m.code])]);
                } else {
                  onChange(markets.filter(x => x !== m.code));
                }
              }}
              className="w-4 h-4 accent-[#1B5E37]"
            />
            <span className="text-sm">{m.label}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Избери в кои пазари да се показва. Без избор = вижда се навсякъде.
      </p>
    </div>
  );
}

function PropertyModal({ open, data, onClose, onSubmit }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '', description: '', description_el: '', description_tr: '', description_en: '', location: '', latitude: '', longitude: '',
    status: 'active', category: 'residential', main_image: '', images: [], documents: [], social_links: []
  });
  const [fileRecords, setFileRecords] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        name: data.name || '',
        description: data.description || '',
        description_el: data.description_el || '',
        description_tr: data.description_tr || '',
        description_en: data.description_en || '',
        location: data.location || '',
        latitude: data.latitude?.toString() || '',
        longitude: data.longitude?.toString() || '',
        status: data.status || 'active',
        category: data.category || 'residential',
        main_image: data.main_image || '',
        images: data.images || [],
        documents: data.documents || [],
        social_links: data.social_links || []
      });
      // Load files from API
      if (data.id) {
        axios.get(`${API}/files?entity_id=${data.id}`).then(res => {
          setFileRecords(res.data || []);
        }).catch(() => setFileRecords([]));
      }
    } else {
      setFormData({ name: '', description: '', description_el: '', description_tr: '', description_en: '', location: '', latitude: '', longitude: '', status: 'active', category: 'residential', main_image: '', images: [], documents: [], social_links: [] });
      setFileRecords([]);
    }
  }, [data, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data ? t('props.edit_project') : t('props.new_project')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Име на проекта *</Label>
            <Input value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div>
            <Label>{t('admin.description')} *</Label>
            <MultilingualDescriptionEditor
              values={{ bg: formData.description, el: formData.description_el, tr: formData.description_tr, en: formData.description_en }}
              onChange={(lang, text) => setFormData(f => ({ ...f, [lang === 'bg' ? 'description' : `description_${lang}`]: text }))}
              context={`Проект: ${formData.name}, Локация: ${formData.location}`}
              placeholder="Генерирай описание за жилищен комплекс в София"
              richText
            />
          </div>
          <div>
            <Label>{t('admin.location')} *</Label>
            <Input value={formData.location} onChange={(e) => setFormData(f => ({ ...f, location: e.target.value }))} required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Latitude</Label>
              <Input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData(f => ({ ...f, latitude: e.target.value }))} />
            </div>
            <div>
              <Label>Longitude</Label>
              <Input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData(f => ({ ...f, longitude: e.target.value }))} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.status')}</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">{t('props.status.active')}</SelectItem>
                  <SelectItem value="upcoming">{t('props.status.upcoming')}</SelectItem>
                  <SelectItem value="completed">{t('props.status.completed')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('props.category')}</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">{t('props.cat.residential')}</SelectItem>
                  <SelectItem value="commercial">{t('props.cat.commercial')}</SelectItem>
                  <SelectItem value="hotel">{t('props.cat.hotel')}</SelectItem>
                  <SelectItem value="other">Други</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Пазари / Markets */}
          <div className="border border-border rounded-xl p-4 bg-[#1B5E37]/5">
            <MarketsSelector
              value={formData.markets}
              onChange={(v) => setFormData(f => ({ ...f, markets: v }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Left col: Images */}
            <div>
              <Label className="mb-2 block">Снимки <span className="text-xs text-muted-foreground font-normal">{t('props.images_hint')}</span></Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.images?.length > 0 ? formData.images : (formData.main_image ? [formData.main_image] : [])).map((img, idx) => (
                  <div
                    key={idx}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData('text/plain', idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = parseInt(e.dataTransfer.getData('text/plain'));
                      const imgs = [...(formData.images?.length > 0 ? formData.images : [formData.main_image])];
                      const [moved] = imgs.splice(from, 1);
                      imgs.splice(idx, 0, moved);
                      setFormData(f => ({ ...f, images: imgs, main_image: imgs[0] || '' }));
                    }}
                    className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-border group cursor-grab active:cursor-grabbing"
                    style={idx === 1 ? { borderColor: '#1B5E37' } : idx === 0 ? { borderColor: '#6b7280' } : {}}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" onError={(e) => { 
                      e.target.style.display='none'; 
                      e.target.parentElement.style.background='#fee2e2';
                      e.target.parentElement.setAttribute('title','Счупена снимка — изтрий я');
                      const icon = document.createElement('span');
                      icon.innerHTML='⚠️';
                      icon.style.cssText='position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:20px';
                      e.target.parentElement.appendChild(icon);
                    }} />
                    {idx === 0 && (
                      <span className="absolute bottom-0 left-0 right-0 bg-gray-700 text-white text-[9px] text-center py-0.5 font-medium">LOGO</span>
                    )}
                    {idx === 1 && (
                      <span className="absolute bottom-0 left-0 right-0 bg-[#1B5E37] text-white text-[9px] text-center py-0.5 font-medium">COVER</span>
                    )}
                    <button type="button"
                      onClick={() => {
                        const imgs = [...(formData.images?.length > 0 ? formData.images : [formData.main_image])];
                        imgs.splice(idx, 1);
                        setFormData(f => ({ ...f, images: imgs, main_image: imgs[0] || '' }));
                      }}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-xs"
                    >✕</button>
                  </div>
                ))}
                <label className="w-20 h-20 rounded-lg border-2 border-dashed border-border hover:border-[#1B5E37] flex flex-col items-center justify-center cursor-pointer transition-colors gap-1">
                  <input type="file" className="hidden" accept="image/*" multiple
                    onChange={async (e) => {
                      const files = Array.from(e.target.files);
                      setUploading(true);
                      for (const file of files) {
                        const fd = new FormData();
                        fd.append('file', file);
                        try {
                          const r = await axios.post(`${API}/upload?category=property&entity_id=${data?.id || 'new'}`, fd);
                          const url = r.data.url?.startsWith('http') ? r.data.url : `${API}${r.data.url}`;
                          setFormData(f => {
                            const imgs = [...(f.images?.length > 0 ? f.images : (f.main_image ? [f.main_image] : [])), url];
                            return { ...f, images: imgs, main_image: imgs[0] || '' };
                          });
                        } catch { toast.error('Грешка при качване'); }
                      }
                      setUploading(false);
                      toast.success('Снимките са качени');
                    }}
                  />
                  <Upload className="w-4 h-4 text-muted-foreground" />
                  <span className="text-[10px] text-muted-foreground">Качи</span>
                </label>
              </div>
            </div>
            {/* Right col: Documents */}
            <div>
              <Label className="mb-2 block">{t('props.documents')}</Label>
              {fileRecords.filter(f => !f.content_type?.startsWith('image/')).length === 0 && (
                <p className="text-xs text-muted-foreground py-2">Няма качени документи</p>
              )}
              <div className="space-y-1.5 mb-2">
                {fileRecords.filter(f => !f.content_type?.startsWith('image/')).map((file, idx) => (
                  <div key={file.id} className="flex items-center gap-2 p-2 rounded-lg border bg-blue-50/50 group">
                    <FileText className="w-4 h-4 text-blue-600 flex-none" />
                    <span className="flex-1 text-xs text-blue-700 truncate">
                      {file.original_filename || file.original_name || `Документ ${idx + 1}`}
                    </span>
                    <button type="button"
                      onClick={async () => {
                        try {
                          await axios.delete(`${API}/files/${file.id}`);
                          setFileRecords(fr => fr.filter(f => f.id !== file.id));
                          toast.success('Изтрит');
                        } catch { toast.error('Грешка'); }
                      }}
                      className="w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity flex-none"
                    >✕</button>
                  </div>
                ))}
              </div>
              <label className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-border hover:bg-secondary transition-colors cursor-pointer">
                <input type="file" className="hidden" accept=".pdf,.doc,.docx"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (!data?.id) { toast.error('Запази проекта първо, после качи документи'); return; }
                    setUploading(true);
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      await axios.post(`${API}/upload?category=document&entity_id=${data.id}`, fd);
                      const res = await axios.get(`${API}/files?entity_id=${data.id}`);
                      setFileRecords(res.data || []);
                      toast.success(`${file.name} качен`);
                    } catch { toast.error('Грешка при качване'); }
                    setUploading(false);
                  }}
                />
                <FileText className="w-3 h-3 text-blue-600" /> + Документ
              </label>
            </div>
          </div>
          {/* Social Links */}
          <div className="border border-border rounded-xl p-4">
            <Label className="mb-3 block font-medium">{t('props.social_networks')}</Label>
            <p className="text-xs text-muted-foreground mb-3">Добавете URL към Facebook, Instagram, YouTube и др. Ще се показват като бутони в сайта.</p>
            <div className="space-y-2 mb-3">
              {(formData.social_links || []).map((link, idx) => {
                const domain = (() => { try { return new URL(link).hostname.replace('www.',''); } catch { return ''; } })();
                const icon = domain.includes('facebook') ? '📘' : domain.includes('instagram') ? '📷' : domain.includes('youtube') ? '▶️' : domain.includes('tiktok') ? '🎵' : domain.includes('linkedin') ? '💼' : domain.includes('twitter') || domain.includes('x.com') ? '🐦' : '🔗';
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg flex-none">{icon}</span>
                    <input
                      type="url"
                      value={link}
                      onChange={e => {
                        const links = [...(formData.social_links || [])];
                        links[idx] = e.target.value;
                        setFormData(f => ({ ...f, social_links: links }));
                      }}
                      placeholder="https://facebook.com/..."
                      className="flex-1 text-sm border border-input rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button type="button" onClick={() => setFormData(f => ({ ...f, social_links: (f.social_links||[]).filter((_,i)=>i!==idx) }))}
                      className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                  </div>
                );
              })}
            </div>
            <button type="button"
              onClick={() => setFormData(f => ({ ...f, social_links: [...(f.social_links||[]), ''] }))}
              className="text-sm text-[#1B5E37] hover:underline flex items-center gap-1">
              + Добави линк
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={uploading}>{uploading ? t('props.uploading') : data ? t('admin.save') : t('props.create')}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Building Modal Component
function BuildingModal({ open, data, propertyId, onClose, onSubmit }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ property_id: '', name: '', floors_count: 1, description: '', blueprint_image: '', images: [] });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({ property_id: data.property_id, name: data.name, floors_count: data.floors_count, description: data.description || '', blueprint_image: data.blueprint_image || '', images: data.images || [] });
    } else {
      setFormData({ property_id: propertyId || '', name: '', floors_count: 1, description: '', blueprint_image: '', images: [] });
    }
  }, [data, propertyId, open]);

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };

  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await axios.post(`${API}/upload?category=building&entity_id=${data?.id || 'new'}`, fd);
        const url = r.data.url?.startsWith('http') ? r.data.url : `${API}${r.data.url}`;
        if (type === 'blueprint') {
          setFormData(f => ({ ...f, blueprint_image: url }));
        } else {
          setFormData(f => ({ ...f, images: [...(f.images || []), url] }));
        }
      } catch { toast.error('Грешка при качване'); }
    }
    setUploading(false);
    toast.success('Качено успешно');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{data ? t('props.edit_building') : t('props.new_building')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Име на сградата *</Label>
            <Input value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} required placeholder="Сграда A" />
          </div>
          <div>
            <Label>{t('props.floors_count')} *</Label>
            <Input type="number" min="1" value={formData.floors_count} onChange={(e) => setFormData(f => ({ ...f, floors_count: parseInt(e.target.value) }))} required />
          </div>
          <div>
            <Label>{t('admin.description')}</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Описание на сградата, входа, особености..." />
          </div>
          <div>
            <Label>{t('props.blueprint')}</Label>
            <div className="mt-1 flex items-center gap-3">
              {formData.blueprint_image ? (
                <div className="relative group">
                  <img src={formData.blueprint_image} alt="Blueprint" className="w-24 h-24 object-cover rounded border" />
                  <button type="button" onClick={() => setFormData(f => ({ ...f, blueprint_image: '' }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ) : null}
              <label className="w-24 h-24 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'blueprint')} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground text-center">Качи чертеж</span>
              </label>
            </div>
          </div>
          <div>
            <Label>{t('props.building_photos')}</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(formData.images || []).map((img, idx) => (
                <div key={idx} className="relative group w-20 h-20">
                  <img src={img} alt={`img-${idx}`} className="w-full h-full object-cover rounded border" />
                  <button type="button" onClick={() => setFormData(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
              <label className="w-20 h-20 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => handleImageUpload(e, 'images')} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Добави</span>
              </label>
            </div>
          </div>
          {/* Social Links */}
          <div className="border border-border rounded-xl p-4">
            <Label className="mb-3 block font-medium">{t('props.social_networks')}</Label>
            <p className="text-xs text-muted-foreground mb-3">Добавете URL към Facebook, Instagram, YouTube и др. Ще се показват като бутони в сайта.</p>
            <div className="space-y-2 mb-3">
              {(formData.social_links || []).map((link, idx) => {
                const domain = (() => { try { return new URL(link).hostname.replace('www.',''); } catch { return ''; } })();
                const icon = domain.includes('facebook') ? '📘' : domain.includes('instagram') ? '📷' : domain.includes('youtube') ? '▶️' : domain.includes('tiktok') ? '🎵' : domain.includes('linkedin') ? '💼' : domain.includes('twitter') || domain.includes('x.com') ? '🐦' : '🔗';
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg flex-none">{icon}</span>
                    <input
                      type="url"
                      value={link}
                      onChange={e => {
                        const links = [...(formData.social_links || [])];
                        links[idx] = e.target.value;
                        setFormData(f => ({ ...f, social_links: links }));
                      }}
                      placeholder="https://facebook.com/..."
                      className="flex-1 text-sm border border-input rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button type="button" onClick={() => setFormData(f => ({ ...f, social_links: (f.social_links||[]).filter((_,i)=>i!==idx) }))}
                      className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                  </div>
                );
              })}
            </div>
            <button type="button"
              onClick={() => setFormData(f => ({ ...f, social_links: [...(f.social_links||[]), ''] }))}
              className="text-sm text-[#1B5E37] hover:underline flex items-center gap-1">
              + Добави линк
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={uploading}>{uploading ? 'Качване...' : data ? 'Запази' : 'Създай'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Floor Modal Component
function FloorModal({ open, data, buildingId, onClose, onSubmit }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ building_id: '', floor_number: 1, apartments_count: 4, description: '', blueprint_image: '', images: [] });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({ building_id: data.building_id, floor_number: data.floor_number, apartments_count: data.apartments_count, description: data.description || '', blueprint_image: data.blueprint_image || '', images: data.images || [] });
    } else {
      setFormData({ building_id: buildingId || '', floor_number: 1, apartments_count: 4, description: '', blueprint_image: '', images: [] });
    }
  }, [data, buildingId, open]);

  const handleSubmit = (e) => { e.preventDefault(); onSubmit(formData); };

  const handleImageUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append('file', file);
      try {
        const r = await axios.post(`${API}/upload?category=floor&entity_id=${data?.id || 'new'}`, fd);
        const url = r.data.url?.startsWith('http') ? r.data.url : `${API}${r.data.url}`;
        if (type === 'blueprint') setFormData(f => ({ ...f, blueprint_image: url }));
        else setFormData(f => ({ ...f, images: [...(f.images || []), url] }));
      } catch { toast.error('Грешка при качване'); }
    }
    setUploading(false);
    toast.success('Качено успешно');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{data ? t('props.edit_floor') : t('props.new_floor')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('props.floor_number')} *</Label>
              <Input type="number" min="0" value={formData.floor_number} onChange={(e) => setFormData(f => ({ ...f, floor_number: parseInt(e.target.value) }))} required />
            </div>
            <div>
              <Label>{t('props.apts_count')}</Label>
              <Input type="number" min="1" value={formData.apartments_count} onChange={(e) => setFormData(f => ({ ...f, apartments_count: parseInt(e.target.value) }))} />
            </div>
          </div>
          <div>
            <Label>{t('props.floor_description')}</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))} rows={2} placeholder="Напр. Приземен етаж с търговски обекти, паркинг..." />
          </div>
          <div>
            <Label>План / Разпределение на етажа</Label>
            <div className="mt-1 flex items-center gap-3">
              {formData.blueprint_image ? (
                <div className="relative group">
                  <img src={formData.blueprint_image} alt="Floor plan" className="w-32 h-24 object-contain rounded border bg-gray-50 p-1" />
                  <button type="button" onClick={() => setFormData(f => ({ ...f, blueprint_image: '' }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ) : null}
              <label className="w-32 h-24 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e, 'blueprint')} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground text-center">Качи план на етажа</span>
              </label>
            </div>
          </div>
          <div>
            <Label>{t('props.floor_photos')}</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(formData.images || []).map((img, idx) => (
                <div key={idx} className="relative group w-20 h-20">
                  <img src={img} alt={`img-${idx}`} className="w-full h-full object-cover rounded border" />
                  <button type="button" onClick={() => setFormData(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
              <label className="w-20 h-20 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" multiple onChange={(e) => handleImageUpload(e, 'images')} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Добави</span>
              </label>
            </div>
          </div>
          {/* Social Links */}
          <div className="border border-border rounded-xl p-4">
            <Label className="mb-3 block font-medium">{t('props.social_networks')}</Label>
            <p className="text-xs text-muted-foreground mb-3">Добавете URL към Facebook, Instagram, YouTube и др. Ще се показват като бутони в сайта.</p>
            <div className="space-y-2 mb-3">
              {(formData.social_links || []).map((link, idx) => {
                const domain = (() => { try { return new URL(link).hostname.replace('www.',''); } catch { return ''; } })();
                const icon = domain.includes('facebook') ? '📘' : domain.includes('instagram') ? '📷' : domain.includes('youtube') ? '▶️' : domain.includes('tiktok') ? '🎵' : domain.includes('linkedin') ? '💼' : domain.includes('twitter') || domain.includes('x.com') ? '🐦' : '🔗';
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-lg flex-none">{icon}</span>
                    <input
                      type="url"
                      value={link}
                      onChange={e => {
                        const links = [...(formData.social_links || [])];
                        links[idx] = e.target.value;
                        setFormData(f => ({ ...f, social_links: links }));
                      }}
                      placeholder="https://facebook.com/..."
                      className="flex-1 text-sm border border-input rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button type="button" onClick={() => setFormData(f => ({ ...f, social_links: (f.social_links||[]).filter((_,i)=>i!==idx) }))}
                      className="text-red-400 hover:text-red-600 text-xs px-1">✕</button>
                  </div>
                );
              })}
            </div>
            <button type="button"
              onClick={() => setFormData(f => ({ ...f, social_links: [...(f.social_links||[]), ''] }))}
              className="text-sm text-[#1B5E37] hover:underline flex items-center gap-1">
              + Добави линк
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={uploading}>{uploading ? 'Качване...' : data ? 'Запази' : 'Създай'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Apartment Modal Component
function ApartmentModal({ open, data, floorId, buildingId, propertyId, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    floor_id: '', building_id: '', property_id: '', number: '', area: 50, rooms: 2, price: 100000,
    status: 'available', description: '', blueprint_image: '', images: [], features: []
  });

  useEffect(() => {
    if (data) {
      setFormData({
        floor_id: data.floor_id, building_id: data.building_id, property_id: data.property_id,
        number: data.number, area: data.area, rooms: data.rooms, price: data.price,
        status: data.status, description: data.description || '', blueprint_image: data.blueprint_image || '',
        images: data.images || [], features: data.features || []
      });
    } else {
      setFormData({
        floor_id: floorId || '', building_id: buildingId || '', property_id: propertyId || '',
        number: '', area: 50, rooms: 2, price: 100000, status: 'available', description: '',
        blueprint_image: '', images: [], features: []
      });
    }
  }, [data, floorId, buildingId, propertyId, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{data ? t('props.edit_apt') : t('props.new_apt')}</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('props.apt_number')} *</Label>
              <Input value={formData.number} onChange={(e) => setFormData(f => ({ ...f, number: e.target.value }))} required placeholder="101" />
            </div>
            <div>
              <Label>Статус</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Свободен</SelectItem>
                  <SelectItem value="reserved">Резервиран</SelectItem>
                  <SelectItem value="sold">Продаден</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Площ (м²) *</Label>
              <Input type="number" step="0.1" min="1" value={formData.area} onChange={(e) => setFormData(f => ({ ...f, area: parseFloat(e.target.value) }))} required />
            </div>
            <div>
              <Label>Стаи *</Label>
              <Input type="number" min="1" value={formData.rooms} onChange={(e) => setFormData(f => ({ ...f, rooms: parseInt(e.target.value) }))} required />
            </div>
            <div>
              <Label>Цена (€) *</Label>
              <Input type="number" min="0" value={formData.price} onChange={(e) => setFormData(f => ({ ...f, price: parseFloat(e.target.value) }))} required />
            </div>
          </div>
          <div>
            <Label>{t('admin.description')}</Label>
            <Textarea value={formData.description} onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))} rows={2} />
            <AiTextHelper 
              value={formData.description} 
              onResult={(text) => setFormData(f => ({ ...f, description: text }))}
              context={`Апартамент ${formData.number}, ${formData.area}м², ${formData.rooms} стаи, цена: ${formData.price}€`}
              placeholder="Генерирай описание на апартамент"
            />
          </div>
          <div>
            <Label>Характеристики (разделени със запетая)</Label>
            <Input 
              value={formData.features.join(', ')} 
              onChange={(e) => setFormData(f => ({ ...f, features: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))} 
              placeholder="Балкон, Паркомясто, Изба"
            />
          </div>
          <div>
            <Label>Чертеж / Разпределение на апартамента</Label>
            <div className="mt-1 flex items-center gap-3">
              {formData.blueprint_image ? (
                <div className="relative group">
                  <img src={formData.blueprint_image} alt="Blueprint" className="w-32 h-24 object-contain rounded border bg-gray-50 p-1" />
                  <button type="button" onClick={() => setFormData(f => ({ ...f, blueprint_image: '' }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ) : null}
              <label className="w-32 h-24 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" onChange={async (e) => {
                  const fd = new FormData(); fd.append('file', e.target.files[0]);
                  try {
                    const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload?category=apartment&entity_id=${data?.id || 'new'}`, fd);
                    const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}/api${r.data.url}`;
                    setFormData(f => ({ ...f, blueprint_image: url }));
                    toast.success('Чертежът е качен');
                  } catch { toast.error('Грешка при качване'); }
                }} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground text-center">Качи разпределение</span>
              </label>
            </div>
          </div>
          <div>
            <Label>Снимки на апартамента</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {(formData.images || []).map((img, idx) => (
                <div key={idx} className={`relative group w-20 h-20 ${idx === 0 ? 'ring-2 ring-primary' : ''}`}>
                  <img src={img} alt={`apt-${idx}`} className="w-full h-full object-cover rounded border" />
                  {idx === 0 && <span className="absolute bottom-0 left-0 right-0 bg-primary text-white text-[8px] text-center py-0.5 rounded-b">COVER</span>}
                  <button type="button" onClick={() => setFormData(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }))}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100">✕</button>
                </div>
              ))}
              <label className="w-20 h-20 rounded border-2 border-dashed border-border hover:border-primary flex flex-col items-center justify-center cursor-pointer gap-1">
                <input type="file" className="hidden" accept="image/*" multiple onChange={async (e) => {
                  const files = Array.from(e.target.files);
                  for (const file of files) {
                    const fd = new FormData(); fd.append('file', file);
                    try {
                      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/upload?category=apartment&entity_id=${data?.id || 'new'}`, fd);
                      const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}/api${r.data.url}`;
                      setFormData(f => ({ ...f, images: [...(f.images || []), url] }));
                    } catch { toast.error('Грешка при качване'); }
                  }
                  toast.success('Снимките са качени');
                }} />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Добави</span>
              </label>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Първата снимка е Cover снимката</p>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit">{data ? 'Запази' : 'Създай'}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
